import type {
  CordialityCheckResult,
  LinkCheckResult,
  LinkMode,
  LinkPolicyDTO,
  LinkRequestDTO,
  ProductLinkDTO,
  CordialityReviewDTO,
  CordialityStatsDTO,
  BotQuestionDTO,
  ChannelAccountDTO,
  ConversationDetailDTO,
  ConversationListItemDTO,
  ConversationStatus,
  CustomerDTO,
  MeDTO,
  MessageDTO,
  OrderDTO,
  OrderStatusTone,
  Priority,
  ProductDTO,
  ChannelAutoReplyDTO,
  ChannelType,
  LogPageDTO,
  QuickReplyDTO,
  StoreDTO,
  TicketDTO,
} from '@sac/contracts'

export interface CrmCustomerDTO {
  id: string
  displayName: string
  fullName: string | null
  email: string | null
  phone: string | null
  city: string | null
  /** Masked, e.g. `***.982.247-**`. Full value is a separate audited endpoint. */
  cpf: string | null
  hasCpf: boolean
  avatarColor: string
  marketingConsent: boolean
  suppressedAt: string | null
  createdAt: string
  orderCount: number
  totalSpent: number
  channels: string[]
}

class ApiError extends Error {
  status: number
  /** A frase em português que o servidor mandou, quando mandou. `message` continua sendo o código,
   *  que é do que as telas antigas dependem para decidir o que dizer. */
  detail: string | null
  constructor(status: number, message: string, detail: string | null = null) {
    super(message)
    this.status = status
    this.detail = detail
  }
}

function qsOf(params: Record<string, string | number | undefined>) {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') qs.set(k, String(v))
  return qs
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Fastify's JSON body parser 400s on an empty body sent with a JSON content-type — only
  // attach the header when there's actually a body (POSTs like /read, /logout have none).
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string> | undefined) }
  if (init?.body) headers['Content-Type'] = 'application/json'
  const res = await fetch(path, { ...init, credentials: 'include', headers })
  if (!res.ok) {
    let message = res.statusText
    let detail: string | null = null
    try {
      const body = await res.json()
      message = body.error ?? message
      detail = typeof body.message === 'string' ? body.message : null
    } catch {
      // ignore
    }
    throw new ApiError(res.status, message, detail)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const api = {
  login: (email: string, password: string) =>
    request<MeDTO>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  botQuestions: (status: 'pending' | 'answered' = 'pending') =>
    request<BotQuestionDTO[]>(`/api/bot-questions?status=${status}`),
  answerBotQuestion: (id: string, optionId: string) =>
    request<BotQuestionDTO>(`/api/bot-questions/${id}/answer`, {
      method: 'POST',
      body: JSON.stringify({ optionId }),
    }),
  crmStats: () =>
    request<{ total: number; withCpf: number; withEmail: number; withPhone: number; consented: number; suppressed: number; purchasers: number }>(
      '/api/crm/stats',
    ),
  orders: (f: { q?: string; channel?: string; storeId?: string; contact?: string; limit?: number; offset?: number }) => {
    const p = new URLSearchParams()
    if (f.q) p.set('q', f.q)
    if (f.channel) p.set('channel', f.channel)
    if (f.storeId) p.set('storeId', f.storeId)
    if (f.contact) p.set('contact', f.contact)
    if (f.limit) p.set('limit', String(f.limit))
    if (f.offset) p.set('offset', String(f.offset))
    return request<OrderRowDTO[]>(`/api/orders?${p}`)
  },
  kpis: (days: number) => request<KpiReportDTO>(`/api/analytics/kpis?days=${days}`),
  orderStats: () =>
    request<{ total: number; revenue: number; contactedBefore: number; contactedAfter: number; noContact: number }>(
      '/api/orders/stats',
    ),
  crmCustomers: (f: { q?: string; hasCpf?: string; consent?: string; contacted?: string }) => {
    const p = new URLSearchParams()
    if (f.q) p.set('q', f.q)
    if (f.hasCpf) p.set('hasCpf', f.hasCpf)
    if (f.consent) p.set('consent', f.consent)
    if (f.contacted) p.set('contacted', f.contacted)
    return request<CrmCustomerDTO[]>(`/api/crm/customers?${p}`)
  },
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  me: () => request<MeDTO>('/api/auth/me'),

  conversations: (params: Record<string, string | undefined>) => {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v)
    return request<ConversationListItemDTO[]>(`/api/conversations?${qs.toString()}`)
  },
  conversation: (id: string) => request<ConversationDetailDTO>(`/api/conversations/${id}`),
  patchConversation: (
    id: string,
    body: { status?: ConversationStatus; assignedAgentId?: string | null; priority?: Priority },
  ) => request<{ ok: true }>(`/api/conversations/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  markRead: (id: string) => request<{ ok: true }>(`/api/conversations/${id}/read`, { method: 'POST' }),
  markUnread: (id: string) => request<{ ok: true }>(`/api/conversations/${id}/unread`, { method: 'POST' }),
  dispatchNotices: () => request<DispatchCandidateDTO[]>('/api/orders/dispatch-notices'),
  sendDispatchNotices: (orderIds: string[]) =>
    request<{ sent: number; failed: number; results: { orderId: string; sent: boolean; error: string | null }[] }>(
      '/api/orders/dispatch-notices/send',
      { method: 'POST', body: JSON.stringify({ orderIds }) },
    ),
  refreshDelivery: (orderId: string) =>
    request<{ refreshed: boolean; error: string | null; trackingStatus: string | null; checkedAt: string | null }>(
      `/api/orders/${orderId}/refresh-delivery`,
      { method: 'POST' },
    ),
  sendMessage: (
    id: string,
    body: {
      text?: string
      clientMessageId?: string
      productIds?: string[]
      aiVerbatim?: boolean
      images?: { dataUrl: string; filename: string }[]
      cordialityReviewId?: string
    },
  ) =>
    request<MessageDTO[]>(`/api/conversations/${id}/messages`, { method: 'POST', body: JSON.stringify(body) }),
  sendNote: (id: string, text: string) =>
    request<MessageDTO>(`/api/conversations/${id}/internal-notes`, { method: 'POST', body: JSON.stringify({ text }) }),
  retryMessage: (messageId: string) => request<MessageDTO>(`/api/messages/${messageId}/retry`, { method: 'POST' }),

  linkIdentity: (conversationId: string, identityId: string) =>
    request<{ ok: true }>(`/api/conversations/${conversationId}/identities/${identityId}/link`, { method: 'POST' }),
  rejectIdentity: (conversationId: string, identityId: string) =>
    request<{ ok: true }>(`/api/conversations/${conversationId}/identities/${identityId}/reject`, { method: 'POST' }),

  tickets: (params: { customerId?: string; status?: string; openOnly?: boolean } = {}) => {
    const q = new URLSearchParams()
    if (params.customerId) q.set('customerId', params.customerId)
    if (params.status) q.set('status', params.status)
    if (params.openOnly) q.set('openOnly', 'true')
    const qs = q.toString()
    return request<TicketDTO[]>(`/api/tickets${qs ? `?${qs}` : ''}`)
  },
  createTicket: (body: {
    customerId: string
    conversationId?: string | null
    orderId?: string | null
    subject: string
    description?: string | null
    assignedAgentId?: string | null
    priority?: string
  }) => request<TicketDTO>('/api/tickets', { method: 'POST', body: JSON.stringify(body) }),
  updateTicket: (
    id: string,
    body: {
      status?: string
      priority?: string
      assignedAgentId?: string | null
      subject?: string
      description?: string | null
      orderId?: string | null
    },
  ) => request<TicketDTO>(`/api/tickets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  /** Reads the pre-computed suggestion. Never triggers generation — see suggestionQueue. */
  storedSuggestion: (conversationId: string) =>
    request<StoredSuggestionDTO | null>(`/api/conversations/${conversationId}/suggestion`),
  suggest: (conversationId: string) =>
    request<SuggestionDTO>(`/api/conversations/${conversationId}/suggest`, { method: 'POST' }),
  suggestFeedback: (
    conversationId: string,
    body: {
      suggestionText: string
      outcome: 'sent_verbatim' | 'edited' | 'rewritten' | 'rejected'
      finalText?: string | null
      knowledgeEntryIds: string[]
      model?: string | null
      // What the agent actually saw when deciding — the number future calibration checks
      // this outcome against.
      certainty?: number | null
      certaintyBand?: 'low' | 'medium' | 'high' | null
    },
  ) => request<{ ok: true }>(`/api/conversations/${conversationId}/suggest/feedback`, { method: 'POST', body: JSON.stringify(body) }),
  reminders: (conversationId: string) =>
    request<{ pending: ReminderDTO[]; proposal: ReminderProposalDTO | null }>(`/api/conversations/${conversationId}/reminders`),
  createReminder: (conversationId: string, body: { dueAt: string; note: string; source?: string }) =>
    request<ReminderDTO>(`/api/conversations/${conversationId}/reminders`, { method: 'POST', body: JSON.stringify(body) }),
  cancelReminder: (conversationId: string, reminderId: string) =>
    request<{ ok: true }>(`/api/conversations/${conversationId}/reminders/${reminderId}`, { method: 'DELETE' }),
  knowledge: (params: { scope?: string; status?: string; q?: string } = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString()
    return request<KnowledgeEntryDTO[]>(`/api/knowledge${qs ? `?${qs}` : ''}`)
  },
  users: () => request<TeamUserDTO[]>('/api/config/users'),
  createUser: (body: { name: string; email: string; role: string }) =>
    request<TeamUserDTO & { temporaryPassword: string }>('/api/config/users', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (id: string, body: { name?: string; email?: string; role?: string }) =>
    request<TeamUserDTO>(`/api/config/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  resetUserPassword: (id: string) =>
    request<{ temporaryPassword: string }>(`/api/config/users/${id}/reset-password`, { method: 'POST' }),
  deleteUser: (id: string) => request<{ ok: true }>(`/api/config/users/${id}`, { method: 'DELETE' }),

  integrations: () => request<IntegrationsDTO>('/api/config/integrations'),
  saveIntegration: (provider: string, values: Record<string, string>) =>
    request<{ status: IntegrationStatus[] }>(`/api/config/integrations/${provider}`, {
      method: 'PATCH',
      body: JSON.stringify({ values }),
    }),
  startOauth: (startPath: string, storeId: string) =>
    request<{ url: string }>(`${startPath}?storeId=${encodeURIComponent(storeId)}`),
  testIntegration: (provider: string) =>
    request<{ ok: boolean; message: string }>(`/api/config/integrations/${provider}/test`, { method: 'POST' }),

  whatsappConfig: () => request<WhatsAppConfigDTO>('/api/config/whatsapp/sessions'),
  whatsappQr: (id: string) => request<{ qrCode: string; status: string } | null>(`/api/config/whatsapp/sessions/${id}/qr`),
  whatsappCreateSession: (name: string, channelAccountId?: string | null) =>
    request<{ id: string; name: string; status: string }>('/api/config/whatsapp/sessions', {
      method: 'POST',
      body: JSON.stringify({ name, channelAccountId: channelAccountId ?? null }),
    }),
  whatsappLogout: (id: string) =>
    request<{ ok: true }>(`/api/config/whatsapp/sessions/${id}/logout`, { method: 'POST' }),
  whatsappSessionAction: (id: string, action: 'start' | 'stop') =>
    request<{ ok: true }>(`/api/config/whatsapp/sessions/${id}/${action}`, { method: 'POST' }),
  whatsappDeleteSession: (id: string) =>
    request<{ ok: true }>(`/api/config/whatsapp/sessions/${id}`, { method: 'DELETE' }),
  whatsappLink: (body: { channelAccountId: string; sessionId: string }) =>
    request<{ ok: true }>('/api/config/whatsapp/link', { method: 'POST', body: JSON.stringify(body) }),

  knowledgeStats: () => request<KnowledgeStatsDTO>('/api/knowledge/stats'),
  conversationProduct: (id: string) => request<ConversationProductDTO | null>(`/api/conversations/${id}/product`),
  conversationHistory: (id: string) => request<QuestionHistoryThreadDTO[]>(`/api/conversations/${id}/history`),
  teach: (conversationId: string, body: { question: string; answer: string; sku?: string | null; scope?: string }) =>
    request<{ id: string }>(`/api/conversations/${conversationId}/teach`, { method: 'POST', body: JSON.stringify(body) }),
  patchKnowledge: (id: string, body: { status?: string; answer?: string; scope?: string }) =>
    request<KnowledgeEntryDTO>(`/api/knowledge/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  customer: (id: string) => request<CustomerDTO>(`/api/customers/${id}`),
  customerOrders: (id: string) => request<OrderDTO[]>(`/api/customers/${id}/orders`),

  products: (q?: string) => request<ProductDTO[]>(`/api/products${q ? `?q=${encodeURIComponent(q)}` : ''}`),

  stores: () => request<StoreDTO[]>('/api/stores'),
  channelAccounts: () => request<ChannelAccountDTO[]>('/api/channel-accounts'),
  startConversation: (body: { channelAccountId: string; phone: string; name?: string }) =>
    request<{ ok: true; conversationId: string; existed: boolean; splitRisk: boolean }>('/api/conversations/start', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  setChannelAccountSimulate: (id: string, simulateMode: 'success' | 'delay' | 'fail') =>
    request<{ ok: true }>(`/api/channel-accounts/${id}`, { method: 'PATCH', body: JSON.stringify({ simulateMode }) }),
  agents: () =>
    request<{ id: string; name: string; role: string; avatarColor: string; avatarUrl: string | null }[]>('/api/agents'),
  setAvatar: (userId: string, dataUrl: string | null) =>
    request<{ ok: true }>(`/api/users/${userId}/avatar`, { method: 'PUT', body: JSON.stringify({ dataUrl }) }),
  quickReplies: () => request<QuickReplyDTO[]>('/api/quick-replies'),
  saveQuickReply: (q: { id?: string; title: string; body: string }) =>
    request<QuickReplyDTO>(q.id ? `/api/quick-replies/${q.id}` : '/api/quick-replies', {
      method: q.id ? 'PUT' : 'POST',
      body: JSON.stringify({ title: q.title, body: q.body }),
    }),
  deleteQuickReply: (id: string) => request<{ ok: true }>(`/api/quick-replies/${id}`, { method: 'DELETE' }),

  autoReply: () =>
    request<{ channels: ChannelAutoReplyDTO[]; globallyEnabled: boolean }>('/api/config/auto-reply'),
  setAutoReply: (channelType: ChannelType, body: { enabled: boolean; minCertainty: number }) =>
    request<{ ok: true; enabled: boolean; minCertainty: number }>(
      `/api/config/auto-reply/${channelType}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  /** Registro de respostas. 403 para quem não é admin — a tela nem é oferecida. */
  log: (params: Record<string, string | number | undefined>) => {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') qs.set(k, String(v))
    return request<LogPageDTO>(`/api/log?${qs}`)
  },
  // Prazo no navegador também: passou disso, envia sem avaliar — o servidor já desiste em 2,5 s
  // do Jev, e o resto é a reescrita.
  cordialityCheck: (conversationId: string, text: string) =>
    request<CordialityCheckResult>(`/api/conversations/${conversationId}/cordiality`, {
      method: 'POST',
      body: JSON.stringify({ text }),
      signal: AbortSignal.timeout(10_000),
    }),
  polish: (conversationId: string, text: string) =>
    request<{ text: string }>(`/api/conversations/${conversationId}/polish`, {
      method: 'POST',
      body: JSON.stringify({ text }),
      signal: AbortSignal.timeout(20_000),
    }),
  catalog: () =>
    request<{
      products: { total: number; active: number }
      channels: { channel_type: string; listings: number; skus: number; active: number; last_synced: string | null }[]
      sync: { running: boolean; startedAt: string | null; finishedAt: string | null; error: string | null; results: { label: string; listingsUpserted: number; errors: string[] }[] }
      lastCompletedAt: string | null
      nextAutoAt: string | null
    }>('/api/config/catalog'),
  catalogSync: () => request<unknown>('/api/config/catalog/sync', { method: 'POST', body: JSON.stringify({}) }),
  linkCheck: (conversationId: string, text: string) =>
    request<LinkCheckResult>(`/api/conversations/${conversationId}/link-check`, {
      method: 'POST',
      body: JSON.stringify({ text }),
      signal: AbortSignal.timeout(5_000),
    }),
  linkRequest: (body: { conversationId: string; domains: string[]; sample?: string }) =>
    request<{ scope: string; created: number }>('/api/link-requests', { method: 'POST', body: JSON.stringify(body) }),
  linkRequests: () => request<LinkRequestDTO[]>('/api/link-requests'),
  resolveLinkRequest: (id: string, action: 'allow' | 'deny') =>
    request<{ ok: boolean }>(`/api/link-requests/${id}/${action}`, { method: 'POST' }),
  linkPolicies: () => request<LinkPolicyDTO[]>('/api/link-policies'),
  saveLinkPolicy: (scope: string, body: { mode: LinkMode; domains: string[] }) =>
    request<{ ok: true }>(`/api/link-policies/${scope}`, { method: 'PUT', body: JSON.stringify(body) }),
  productLinks: (conversationId: string, ids: string[]) =>
    request<Record<string, ProductLinkDTO>>(`/api/conversations/${conversationId}/product-links?ids=${ids.join(',')}`),
  cordialityCancel: (reviewId: string) => request<{ ok: boolean }>(`/api/cordiality/${reviewId}/cancel`, { method: 'POST' }),
  cordialityReviews: (params: Record<string, string | number | undefined>) =>
    request<CordialityReviewDTO[]>(`/api/cordiality/reviews?${qsOf(params)}`),
  cordialityStats: (params: Record<string, string | number | undefined>) =>
    request<CordialityStatsDTO>(`/api/cordiality/stats?${qsOf(params)}`),
}

// ---- dev-only: customer simulator + messaging console (bearer token, not session cookie) ----

async function simRequest<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', 'x-sim-token': token, ...(init?.headers ?? {}) },
  })
  if (!res.ok) {
    let message = res.statusText
    let detail: string | null = null
    try {
      const body = await res.json()
      message = body.error ?? message
      detail = typeof body.message === 'string' ? body.message : null
    } catch {
      // ignore
    }
    throw new ApiError(res.status, message, detail)
  }
  return (await res.json()) as T
}

export interface SimCustomer {
  id: string
  displayName: string
  avatarColor: string
}
export interface SimConversation {
  id: string
  channelType: string
  status: string
  storeName: string | null
}

export const simApi = {
  customers: (token: string) => simRequest<SimCustomer[]>('/api/sim/customers', token),
  conversations: (token: string, customerId: string) =>
    simRequest<SimConversation[]>(`/api/sim/customers/${customerId}/conversations`, token),
  messages: (token: string, conversationId: string) =>
    simRequest<MessageDTO[]>(`/api/sim/conversations/${conversationId}/messages`, token),
  send: (token: string, conversationId: string, text: string) =>
    simRequest<MessageDTO>('/api/sim/messages', token, { method: 'POST', body: JSON.stringify({ conversationId, text }) }),
  markRead: (token: string, conversationId: string) =>
    simRequest<{ count: number }>('/api/sim/read', token, { method: 'POST', body: JSON.stringify({ conversationId }) }),
}

export interface DevSummary {
  channelAccounts: {
    id: string
    displayName: string
    storeName: string | null
    channelType: string
    connectionStatus: string
    settings: { simulate?: string; delayMs?: number }
  }[]
  recentMessages: MessageDTO[]
  recentEvents: unknown[]
  enableSim: boolean
  enableDevTools: boolean
}

export const devApi = {
  summary: (token: string) => simRequest<DevSummary>('/api/dev/messaging/summary', token),
  sendCustomerMessage: (token: string, conversationId: string, text: string) =>
    simRequest<MessageDTO>('/api/dev/customer-message', token, {
      method: 'POST',
      body: JSON.stringify({ conversationId, text }),
    }),
}

export { ApiError }


/** Shapes returned by the knowledge harness. Declared here rather than in contracts because
 *  they are read-only view models for the UI, not part of the channel/domain contract. */
export interface SuggestionEntryDTO {
  id: string
  scope: string
  sku: string | null
  question: string
  answer: string
  confidence: number
  via: 'sku' | 'tag' | 'text'
}

export interface CertaintyDTO {
  /** 0-100, or null when the reply is not an attempt to answer (a question back, a gap, or
   *  fixed boilerplate) — there is no claim there to score. */
  value: number | null
  band: 'low' | 'medium' | 'high' | null
  /** Audit trail: every factor that moved the score, in Portuguese, for the tooltip. */
  reasons: string[]
}

export interface SuggestionDTO {
  /**
   * 'internal_gap' = the AI is asking US, not the customer. No customer-facing text exists.
   * 'waiting_customer' = the shop spoke last, nothing pending — no text, nothing to send yet.
   * 'poke' = same, but enough time passed that a check-in message is offered.
   */
  kind: 'answer' | 'internal_gap' | 'customer_info' | 'waiting_customer' | 'poke' | 'needs_human'
  gapQuestion: string | null
  missingFacts: string[]
  confidence: number
  suggestion: string
  entries: SuggestionEntryDTO[]
  sku: string | null
  violations: { code: string; severity: 'block' | 'warn'; detail: string; match: string }[]
  ungrounded: string[]
  ungroundedClaims: string[]
  blocked: boolean
  servedModel: string
  costUsd: number
  /** Phase A structural score. Not yet validated — see the badge's own label in the UI. */
  certainty: CertaintyDTO
}

export interface StoredSuggestionDTO {
  kind: 'answer' | 'internal_gap' | 'customer_info' | 'waiting_customer' | 'poke' | 'needs_human' | 'error'
  suggestion: string
  gapQuestion: string | null
  missingFacts: string[]
  entryIds: string[]
  sku: string | null
  violations: { code: string; severity: 'block' | 'warn'; detail: string; match: string }[]
  ungrounded: string[]
  ungroundedClaims: string[]
  blocked: boolean
  confidence: number | null
  model: string | null
  certainty: number | null
  certaintyBand: 'low' | 'medium' | 'high' | null
  certaintyReasons: string[]
  error: string | null
  /** True when the customer has written again since this was generated. */
  stale: boolean
  createdAt: string
}

export interface KnowledgeEntryDTO {
  id: string
  scope: string
  sku: string | null
  tags: string[]
  question: string
  answer: string
  confidence: number
  useCount: number
  status: string
  sourceMessageIds: string[]
  createdAt: string
}

export interface KnowledgeStatsDTO {
  byScope: { scope: string; status: string; n: number }[]
  outcomes: { outcome: string; n: number }[]
  acceptRate: number | null
  totalFeedback: number
}

export interface QuestionHistoryMessageDTO {
  direction: 'incoming' | 'outgoing'
  text: string
  createdAt: string
}

/** An earlier question from the same buyer, shown above the one being answered. */
export interface QuestionHistoryThreadDTO {
  conversationId: string
  occurredAt: string
  itemId: string | null
  itemTitle: string | null
  /** False when it was about a different listing — still shown, but labelled. */
  sameItem: boolean
  answered: boolean
  messages: QuestionHistoryMessageDTO[]
}

/** Um pedido despachado que ainda não teve aviso enviado ao comprador. */
export interface DispatchCandidateDTO {
  orderId: string
  externalOrderId: string
  buyerName: string
  buyerUserId: string
  carrier: string | null
  dispatchedAt: string
  items: string[]
  text: string
  /** Motivo pelo qual não dá para enviar; nulo quer dizer pronto. */
  blockedReason: string | null
}

export interface ConversationProductDTO {
  sku: string
  externalItemId: string
  title: string
  permalink: string | null
  imageUrl: string | null
  price: number | null
  description: string | null
  attributes: Record<string, string>
}

export interface WhatsAppConfigDTO {
  sessions: {
    id: string
    name: string
    /** qr | connecting | connected | logged_out | stopped */
    status: string
    phone: string | null
    pushName: string | null
    channelAccountId: string | null
    lastError: string | null
    connectedAt: string | null
    /** Whether the socket is up in the running process, as opposed to what the row claims. */
    live: boolean
  }[]
  accounts: { id: string; displayName: string; business: string | null; connectionStatus: string }[]
}

export interface IntegrationFieldDTO {
  key: string
  label: string
  secret: boolean
  placeholder?: string
  hint?: string
  env?: string
}

export interface IntegrationProviderDTO {
  id: string
  label: string
  description: string
  fields: IntegrationFieldDTO[]
  testable: boolean
  /** Present when the provider is connected by authorising a shop instead of pasting keys. */
  oauth?: { startPath: string; channelType: string; needsStore: boolean }
}

export interface ConnectedAccountDTO {
  id: string
  channelType: string
  displayName: string
  externalAccountId: string | null
  connectionStatus: string
  storeId: string | null
  updatedAt: string
}

/** Never carries a secret value — only whether one is set, where from, and its last four chars. */
export interface IntegrationStatus {
  provider: string
  fields: {
    key: string
    configured: boolean
    source: 'database' | 'environment' | null
    value: string | null
    hint: string | null
    unreadable: boolean
  }[]
}

export interface IntegrationsDTO {
  providers: IntegrationProviderDTO[]
  status: IntegrationStatus[]
  accounts: ConnectedAccountDTO[]
  stores: { id: string; name: string }[]
  secretsAvailable: boolean
}

export interface RateDTO {
  value: number | null
  n: number
  suppressed?: 'sample'
}

export interface AttributionGroupDTO {
  conversations: number
  customers: number
  converted: number
  conversionRate: number
  orders: number
  revenue: number
}

export interface KpiReportDTO {
  periodDays: number
  since: string
  volume: { inbound: number; conversations: number; answered: number }
  assertividade: RateDTO & { verbatim: number; edited: number; rejected: number }
  lacunaConhecimento: RateDTO & { gaps: number; byKind: { kind: string; n: number }[] }
  responseTime: {
    viaSac: {
      channelType: string
      answered: number
      answeredElsewhere: number
      unanswered: number
      medianMinutes: number | null
      p90Minutes: number | null
    }[]
  }
  conversao: { preVenda: RateDTO & { converted: number; revenue: number; stillOpen: number } }
  ia: { windowDays: number; assisted: AttributionGroupDTO; unassisted: AttributionGroupDTO; totalAnswered: number }
}

export interface OrderRowDTO {
  id: string
  externalOrderId: string
  channelType: string
  status: string
  statusTone: OrderStatusTone
  total: number
  currency: string
  createdAt: string
  buyerLabel: string | null
  storeName: string | null
  customer: { id: string; name: string | null; avatarColor: string | null }
  product: { name: string; imageUrl: string | null; moreItems: number } | null
  /** before = wrote to us before buying · after = wrote afterwards · none = never wrote. */
  contact: {
    state: 'before' | 'after' | 'none'
    conversationId: string | null
    conversationChannel: string | null
    at: string | null
  }
}

export interface TeamUserDTO {
  /** Data URI da foto; nulo cai nas iniciais sobre avatarColor. */
  avatarUrl: string | null
  id: string
  name: string
  email: string
  role: string
  avatarColor: string
  status: string
  createdAt: string
  messageCount: number
  isSelf: boolean
}

export interface ReminderDTO {
  id: string
  conversationId: string
  dueAt: string
  note: string
  status: string
  source: string
}

/** Read from the customer's own words; never created without a human confirming it. */
export interface ReminderProposalDTO {
  dueAt: string
  /** The fragment that produced the date, e.g. "01/09". */
  matched: string
  /** The sentence it came from, so the agent confirms against evidence and not a bare date. */
  quote: string
}
